import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import { FiUpload, FiFile, FiTrash2, FiExternalLink } from 'react-icons/fi';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.heic,.png,.docx';
const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.82;

function optimizeImage(file) {
  const isImage = file.type.startsWith('image/');
  if (!isImage) return Promise.resolve(file);

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // Skip tiny images that don't need optimization
      const alreadySmall = width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION;
      const alreadyJpeg = file.type === 'image/jpeg';
      if (alreadySmall && alreadyJpeg && file.size < 500 * 1024) {
        return resolve(file);
      }

      // Scale down if needed
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        const ratio = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG (covers PNG → JPEG conversion)
      canvas.toBlob(
        (blob) => {
          const ext = file.name.replace(/\.[^.]+$/, '');
          const optimizedName = `${ext}.jpg`;
          const optimized = new File([blob], optimizedName, { type: 'image/jpeg' });
          resolve(optimized);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fall back to original on error
    };
    img.src = url;
  });
}

export default function DocumentUpload({ contactId, documentType, label }) {
  const { tenant } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    if (!contactId) return;
    try {
      const docs = await api.getContactDocuments(contactId);
      setDocuments(docs.filter(d => d.document_type === documentType));
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [contactId, documentType]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !tenant || !contactId) return;

    setError(null);
    setUploading(true);
    try {
      for (let file of files) {
        if (file.size > 10 * 1024 * 1024) {
          setError(`File "${file.name}" exceeds 10MB limit.`);
          continue;
        }
        file = await optimizeImage(file);
        await api.uploadContactDocument(tenant.id, contactId, file, documentType);
      }
      await fetchDocuments();
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.file_name}"?`)) return;
    try {
      await api.deleteContactDocument(doc.id, doc.file_path);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'heic'].includes(ext)) return '🖼';
    if (ext === 'pdf') return '📄';
    if (ext === 'docx') return '📝';
    return '📎';
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>

      {/* Upload area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (uploading) return;
          const files = Array.from(e.dataTransfer.files);
          if (files.length) handleUpload({ target: { files } });
        }}
        className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          dragOver
            ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/30'
            : 'border-slate-300 dark:border-slate-600 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:border-primary-600 dark:hover:bg-primary-900/20'
        }`}
      >
        <SafeIcon icon={FiUpload} className="h-4 w-4 text-slate-400" />
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {uploading ? 'Uploading...' : dragOver ? 'Drop files here' : 'Click or drag files to upload'}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      <p className="text-xs text-slate-400">
        Supported: PDF, JPG, JPEG, HEIC, PNG, DOCX (max 10MB each). Images are auto-optimized.
      </p>

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      {/* File list */}
      {documents.length > 0 && (
        <div className="space-y-1 mt-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-sm">{getFileIcon(doc.file_name)}</span>
                <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                  {doc.file_name}
                </span>
                <span className="text-xs text-slate-400 shrink-0">
                  {new Date(doc.uploaded_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-slate-400 hover:text-primary-500 transition-colors"
                  title="Open file"
                >
                  <SafeIcon icon={FiExternalLink} className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => handleDelete(doc)}
                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                  title="Delete file"
                >
                  <SafeIcon icon={FiTrash2} className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
